// Hält die Daten des geöffneten Stammbaums im Kunden-Editor:
//   persons       – Liste aller Personen (Name, Geburtsdatum, Foto, Bildbearbeitung …)
//   relationships – Liste der Beziehungen (Eltern–Kind, Partner, Ex-Partner)
//   customColors  – vom Kunden selbst gewählte Familienfarben
// Ein „Hook“ ist eine Funktion, die eine Komponente (hier App.jsx) mit Daten und
// Funktionen zum Ändern versorgt. Jede Änderung wird zusätzlich im Browser
// gespeichert (localStorage), damit nach einem Neuladen nichts verloren geht.
// Das Speichern auf dem Server macht App.jsx (automatisch und per Knopf).
// useTreeData.js
import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildRelationships } from '../utils/treeRules';

// Name, unter dem die Daten im Browser gespeichert werden.
const STORAGE_KEY = 'familytree_data';

// Beim Start: Daten aus dem Browser holen (oder einen leeren Stammbaum).
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { persons: [], relationships: [], customColors: {} };
}

// In den Browser schreiben. Ist der Speicher voll (viele Fotos), wird der Fehler ignoriert.
function saveToStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

export function useTreeData() {
  const [data, setData] = useState(() => loadFromStorage());
  // dataRef enthält immer den neuesten Stand – auch innerhalb von Funktionen, die
  // früher erstellt wurden (React würde dort sonst einen alten Stand sehen).
  const dataRef = useRef(data);

  // Neue Daten übernehmen: merken, neu zeichnen lassen, im Browser speichern.
  const persist = useCallback((newData) => {
    dataRef.current = newData;
    setData(newData);
    saveToStorage(newData);
  }, []);

  // ── Neue Person hinzufügen ─────────────────────────────────────────────
  // relationshipsInput sind die Beziehungen aus dem Formular (z.B. „Kind von …“).
  // Gibt die id der neuen Person zurück.
  const addPerson = useCallback((personData, relationshipsInput) => {
    const rels = Array.isArray(relationshipsInput) ? relationshipsInput : (relationshipsInput ? [relationshipsInput] : []);
    const newPerson = {
      id: uuidv4(),
      name:       personData.name       || '',
      maidenName: personData.maidenName || '',
      birthDate:  personData.birthDate  || '',
      gender:     personData.gender     || 'unknown',
      photo:      personData.photo      || null,
      photoEdit:  personData.photoEdit  || null,
      photoRetouch: personData.photoRetouch || null,
      firstName:  personData.firstName  ?? undefined,
      lastName:   personData.lastName   ?? undefined,
    };

    const currentData = dataRef.current;
    const newRels = buildRelationships(newPerson.id, rels, currentData.relationships);

    persist({
      ...currentData,
      persons:       [...currentData.persons, newPerson],
      relationships: [...currentData.relationships, ...newRels],
    });
    return newPerson.id;
  }, [persist]);

  // ── Person bearbeiten (Stammdaten + Beziehungen) ───────────────────────
  // Alle Beziehungen dieser Person werden neu aus dem Formular aufgebaut.
  // „...data“ übernimmt die übrigen Felder (z.B. selbst gewählte Familienfarben customColors).
  const updatePerson = useCallback((id, personData, relationshipsInput) => {
    const updatedPersons = data.persons.map(p =>
      p.id === id ? { ...p, ...personData } : p
    );

    let updatedRels = data.relationships;

    if (Array.isArray(relationshipsInput)) {
      // Alle bestehenden Beziehungen DIESER Person löschen
      updatedRels = updatedRels.filter(r => r.from !== id && r.to !== id);
      // Neue Beziehungen erstellen
      const newRels = buildRelationships(id, relationshipsInput, updatedRels);
      updatedRels = [...updatedRels, ...newRels];
    }

    persist({ ...data, persons: updatedPersons, relationships: updatedRels });
  }, [data, persist]);

  // ── Person löschen ─────────────────────────────────────────────────────
  // Person und alle ihre Beziehungen entfernen.
  const deletePerson = useCallback((id) => {
    persist({
      ...data,
      persons:       data.persons.filter(p => p.id !== id),
      relationships: data.relationships.filter(r => r.from !== id && r.to !== id),
    });
  }, [data, persist]);

  // Ganzen Stammbaum ersetzen (z.B. nach dem Laden vom Server).
  const importData = useCallback((d) => {
    persist(d);
    saveToStorage(d);
  }, [persist]);
  const clearAll = useCallback(() => persist({ persons: [], relationships: [], customColors: {} }), [persist]);

  // Familienfarbe von Hand ändern. Hat schon eine andere Familie diese Farbe,
  // werden die beiden Farben getauscht.
  const setCustomColor = useCallback((personId, colorIndex, gen1Siblings) => {
    const prev = data.customColors || {};
    const newCustomColors = { ...prev };

    // Finde die aktuelle Farbe jeder Gen1-Person (custom oder Standard)
    const getEffectiveColor = (id) => {
      if (newCustomColors[id] !== undefined) return newCustomColors[id];
      const idx = gen1Siblings ? gen1Siblings.indexOf(id) : -1;
      return idx >= 0 ? idx % 9 : -1;
    };

    // Prüfe ob diese Farbe bereits von jemand anderem belegt ist
    if (gen1Siblings) {
      const otherHolder = gen1Siblings.find(id => id !== personId && getEffectiveColor(id) === colorIndex);
      if (otherHolder) {
        // Tausche: otherHolder bekommt die bisherige Farbe von personId
        const myOldColor = getEffectiveColor(personId);
        newCustomColors[otherHolder] = myOldColor;
      }
    }

    newCustomColors[personId] = colorIndex;
    persist({ ...data, customColors: newCustomColors });
  }, [data, persist]);

  return {
    persons:       data.persons,
    relationships: data.relationships,
    customColors:  data.customColors || {},
    extension:     data.extension || null,   // nur bei einer Erweiterung (siehe utils/extension.js)
    addPerson,
    updatePerson,
    deletePerson,
    importData,
    clearAll,
    setCustomColor,
  };
}
